import { type AtticApiUsageDayFragment, AtticRequestCallerEnum } from "@dashboard/graphql";

/** The ranges the page offers, in UTC days ending today. */
export const RANGE_DAYS = [7, 30, 90] as const;
export type AtticApiUsageRange = (typeof RANGE_DAYS)[number];
export const DEFAULT_RANGE: AtticApiUsageRange = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The caller classes in the order the page lists them. */
export const CALLERS: AtticRequestCallerEnum[] = [
  AtticRequestCallerEnum.ANONYMOUS,
  AtticRequestCallerEnum.CUSTOMER,
  AtticRequestCallerEnum.STAFF,
  AtticRequestCallerEnum.APP,
];

/** The range in the URL, or the default for anything else (a hand-edited link). */
export const parseRange = (days: unknown): AtticApiUsageRange => {
  const value = Number(days);

  return (RANGE_DAYS as readonly number[]).includes(value)
    ? (value as AtticApiUsageRange)
    : DEFAULT_RANGE;
};

const pad = (value: number) => String(value).padStart(2, "0");

const toUtcDay = (date: Date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

/** The last `days` UTC days, today included. */
export const getRangeWindow = (days: number, now: number) => ({
  since: toUtcDay(new Date(now - (days - 1) * DAY_MS)),
  until: toUtcDay(new Date(now)),
});

export interface AtticApiUsageTotals {
  requests: number;
  /** Client (4xx other than 429) and server (5xx) errors together. */
  errors: number;
  throttled: number;
  cost: number;
  /** Top-level mutation fields executed: the writes. */
  writes: number;
  /** Over every request, not a mean of daily means; null for none. */
  meanLatencyMs: number | null;
}

/** The totals of any set of rows. */
export const sumTotals = (rows: AtticApiUsageDayFragment[]): AtticApiUsageTotals => {
  const requests = rows.reduce((sum, row) => sum + row.requests, 0);
  const latency = rows.reduce((sum, row) => sum + row.latencyMs, 0);

  return {
    requests,
    errors: rows.reduce((sum, row) => sum + row.clientErrors + row.serverErrors, 0),
    throttled: rows.reduce((sum, row) => sum + row.throttled, 0),
    cost: rows.reduce((sum, row) => sum + row.cost, 0),
    writes: rows.reduce((sum, row) => sum + row.mutationFields, 0),
    meanLatencyMs: requests > 0 ? latency / requests : null,
  };
};

export interface AtticApiUsageCallerRow extends AtticApiUsageTotals {
  caller: AtticRequestCallerEnum;
  /** This caller's share of all requests, 0 to 1. */
  share: number;
}

/** One row per caller class, in CALLERS order, a class with no traffic at zero. */
export const getCallerRows = (rows: AtticApiUsageDayFragment[]): AtticApiUsageCallerRow[] => {
  const all = rows.reduce((sum, row) => sum + row.requests, 0);

  return CALLERS.map(caller => {
    const totals = sumTotals(rows.filter(row => row.caller === caller));

    return { caller, ...totals, share: all > 0 ? totals.requests / all : 0 };
  });
};

export interface AtticApiUsageAppRow extends AtticApiUsageTotals {
  appId: string;
  /** The last UTC day in the range it made a request. */
  lastDay: string;
}

/** The APP rows per app or API key, most requests first. */
export const getAppRows = (rows: AtticApiUsageDayFragment[]): AtticApiUsageAppRow[] => {
  const byApp = new Map<string, AtticApiUsageDayFragment[]>();

  rows
    .filter(row => row.caller === AtticRequestCallerEnum.APP && row.appId)
    .forEach(row => {
      const appId = row.appId as string;

      byApp.set(appId, [...(byApp.get(appId) ?? []), row]);
    });

  return [...byApp.entries()]
    .map(([appId, appRows]) => ({
      appId,
      ...sumTotals(appRows),
      lastDay: appRows
        .filter(row => row.requests > 0)
        .map(row => String(row.day))
        .reduce((latest, day) => (day > latest ? day : latest), ""),
    }))
    .sort((a, b) => b.requests - a.requests || a.appId.localeCompare(b.appId));
};

/**
 * Requests per UTC day of the window across every caller, oldest first. The
 * API leaves out a day with no requests; this puts it back at zero.
 */
export const getDailyRequests = (
  rows: AtticApiUsageDayFragment[],
  window: { since: string; until: string },
): Array<{ day: string; requests: number }> => {
  const byDay = new Map<string, number>();

  rows.forEach(row => {
    const day = String(row.day);

    byDay.set(day, (byDay.get(day) ?? 0) + row.requests);
  });

  const days: Array<{ day: string; requests: number }> = [];
  const end = Date.parse(`${window.until}T00:00:00Z`);

  for (let at = Date.parse(`${window.since}T00:00:00Z`); at <= end; at += DAY_MS) {
    const day = toUtcDay(new Date(at));

    days.push({ day, requests: byDay.get(day) ?? 0 });
  }

  return days;
};

/** An app's database number from its GraphQL id ("App:12" in base64), or the id as given. */
export const appNumber = (appId: string): string => {
  try {
    const match = /^App:(\d+)$/.exec(atob(appId));

    return match ? match[1] : appId;
  } catch {
    return appId;
  }
};

export type AtticApiUsageAppName =
  | { kind: "loading" }
  | { kind: "apiKey"; label: string; keyId: string }
  | { kind: "app"; label: string; appId: string }
  | { kind: "unknown"; number: string };

/**
 * Who an APP row is: an API key by the App it is (to its key page), another
 * app by id (to its details page), or "App #<n>" for a viewer who may not list
 * apps or one past the first page of names.
 */
export const getAppName = (
  appId: string,
  names: {
    apiKeys: Array<{ id: string; label: string; appId: string }>;
    apps: Array<{ id: string; name: string | null }>;
  } | null,
): AtticApiUsageAppName => {
  const apiKey = names?.apiKeys.find(key => key.appId === appId);

  if (apiKey) {
    return { kind: "apiKey", label: apiKey.label, keyId: apiKey.id };
  }

  const app = names?.apps.find(candidate => candidate.id === appId);

  if (app?.name) {
    return { kind: "app", label: app.name, appId: app.id };
  }

  return { kind: "unknown", number: appNumber(appId) };
};
